import type { SessionPersistence, PersistenceCommand, User } from '../types';

const USER_COOKIE = 'user';
const JWT_COOKIE = 'jwt';
const EXPIRE_DAYS = 183;

interface CookieStore {
  get(name: string): string | undefined;
  set(name: string, value: string, options?: { expires?: number; secure?: boolean; sameSite?: 'strict' | 'lax' | 'none' | 'Strict' | 'Lax' | 'None' }): void;
  remove(name: string): void;
}

/**
 * Normalizes a raw cookie object into a `User`. Handles both the camelCase
 * shape written by our own `saveUser` and the legacy kebab-case shape written
 * by the Rails/client-app backend (e.g. `first-name`, `sso-provider`).
 */
export function deserializeUser(raw: Record<string, unknown>): User {
  return {
    id: raw['id'] as User['id'],
    firstName: (raw['firstName'] ?? raw['first-name']) as string,
    lastName: (raw['lastName'] ?? raw['last-name']) as string,
    homePhone: (raw['homePhone'] ?? raw['home-phone'] ?? '') as string,
    email: raw['email'] as string,
    jwt: raw['jwt'] as string,
    newsletter: raw['newsletter'] as boolean,
    ssoProvider: (raw['ssoProvider'] ?? raw['sso-provider'] ?? null) as string | null,
    hasPassword: (raw['hasPassword'] ?? raw['has-password']) as boolean,
    hideImagelessListings: (raw['hideImagelessListings'] ?? raw['hide-imageless-listings'] ?? false) as boolean,
    locale: (raw['locale'] ?? '') as string,
  };
}

export class CookieSessionPersistence implements SessionPersistence {
  private readonly cookieOptions: { expires: number; secure: boolean; sameSite: 'lax' };

  /**
   * @param cookies        The cookie-jar adapter (typically `js-cookie`).
   * @param secureCookies  When `true`, sets the `Secure` flag so browsers only
   *                       transmit the cookie over HTTPS. The host should pass
   *                       `false` in local development (http://localhost) so
   *                       the browser accepts the cookie at all.
   */
  constructor(private cookies: CookieStore, secureCookies: boolean) {
    this.cookieOptions = { expires: EXPIRE_DAYS, secure: secureCookies, sameSite: 'lax' };
  }

  dispatch(command: PersistenceCommand): void {
    switch (command.type) {
      case 'SAVE_USER':
        this.cookies.set(USER_COOKIE, JSON.stringify(command.user), this.cookieOptions);
        break;
      case 'SAVE_JWT':
        this.cookies.set(JWT_COOKIE, command.jwt, this.cookieOptions);
        break;
      case 'CLEAR':
        this.cookies.remove(USER_COOKIE);
        this.cookies.remove(JWT_COOKIE);
        break;
    }
  }

  loadUser(): User | null {
    const raw = this.cookies.get(USER_COOKIE);
    if (!raw) return null;
    try {
      const user = deserializeUser(JSON.parse(raw) as Record<string, unknown>);
      // The jwt cookie is the source of truth for the token. Merge it back so
      // callers always see a complete User. Falls back to the jwt embedded in
      // the user payload for backwards compatibility with cookies set before
      // the user/jwt split.
      if (!user.jwt) {
        const jwt = this.cookies.get(JWT_COOKIE);
        if (jwt) user.jwt = jwt;
      }
      return user;
    } catch {
      return null;
    }
  }
}
